import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../context/LanguageContext";
import { toast } from "sonner";
import QuickReplyChips from "../components/QuickReplyChips";
import {
  Loader2, ChevronLeft, MessageCircle, Send, Upload, Plus, Trash2,
  CalendarClock, ShieldCheck, AlertTriangle, Trophy, Image as ImageIcon,
} from "lucide-react";

/**
 * Pro Clubs — Fixture Detail (V2).
 * Officers of either team can:
 *   - Chat per-fixture (polls every 8s)
 *   - Submit score + screenshot + goal-scorer breakdown (sum-validated)
 *   - Propose a reschedule; opposing officer accepts/declines
 *   - View confirmed score + scorers after both teams agree
 */
export default function ProClubsFixtureV2() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useT();

  const [fixture, setFixture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("submit");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/proclubs/fixtures/${id}`);
      setFixture(data);
    } catch {
      setFixture({ error: true });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const t2 = setInterval(refresh, 15000);
    return () => clearInterval(t2);
  }, [refresh]);

  if (loading) return <CenterLoader />;
  if (!fixture || fixture.error) {
    return (
      <div className="space-y-4 py-8 text-center">
        <div className="text-sm text-[#71717A]">{t("pcl.fx.notFound")}</div>
        <Link to="/v2/proclubs" className="text-[11px] font-mono text-[#00FF87] underline">{t("pcl.fx.backToHub")}</Link>
      </div>
    );
  }

  const myTeam = fixture.viewer_team;
  const isOfficer = !!myTeam;
  const dt = new Date(fixture.scheduled_at);

  return (
    <div className="space-y-4 pb-8" data-testid="v2-pc-fixture">
      <header className="flex items-center justify-between">
        <Link to="/v2/proclubs" className="text-[11px] font-mono text-[#71717A] hover:text-white inline-flex items-center gap-1">
          <ChevronLeft className="w-3 h-3" /> {t("pcl.fx.backToHub")}
        </Link>
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#00FF87]">
          // {t("pcl.fx.matchday")} {fixture.matchday}
        </div>
      </header>

      {/* Hero */}
      <div className="rounded-3xl p-5 bg-gradient-to-br from-[#0a3a25] via-[#082916] to-[#02100a] ring-1 ring-[#00FF87]/40">
        <div className="text-[10px] font-mono text-[#71717A] flex items-center justify-between">
          <span>{dt.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</span>
          <span>{dt.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
        </div>
        <div className="mt-2 grid grid-cols-3 items-center gap-2">
          <TeamBadge team={fixture.home_team} align="left" highlight={myTeam === "home"} />
          <div className="text-center">
            <div className="text-3xl font-extrabold text-white tabular-nums">
              {fixture.status === "confirmed" ? `${fixture.home_score} - ${fixture.away_score}` : "—"}
            </div>
            <StatusPill status={fixture.status} t={t} />
          </div>
          <TeamBadge team={fixture.away_team} align="right" highlight={myTeam === "away"} />
        </div>
        {!isOfficer && (
          <div className="mt-3 text-[10px] text-center text-[#FFD700] font-mono">
            {t("pcl.fx.readOnly")}
          </div>
        )}
      </div>

      {/* Confirmed result detail */}
      {fixture.status === "confirmed" && (
        <ConfirmedScorersView fixture={fixture} t={t} />
      )}

      {/* Officer tabs */}
      {isOfficer && fixture.status !== "confirmed" && (
        <>
          <nav className="flex gap-2 -mx-1 px-1 overflow-x-auto" role="tablist">
            {[
              ["submit",     t("pcl.fx.tabSubmit"),     ShieldCheck],
              ["chat",       t("pcl.fx.tabChat"),       MessageCircle],
              ["reschedule", t("pcl.fx.tabResch"),      CalendarClock],
            ].map(([k, label, Icon]) => (
              <button
                key={k}
                type="button"
                onClick={() => setTab(k)}
                data-testid={`v2-pc-fx-tab-${k}`}
                className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] font-bold uppercase tracking-widest transition-colors ${
                  tab === k ? "bg-[#00FF87] text-[#06120A] shadow-lg" : "bg-white/5 text-white/70 hover:bg-white/10"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </nav>

          {tab === "submit"     && <SubmitForm fixture={fixture} refresh={refresh} t={t} />}
          {tab === "chat"       && <ChatPanel fixtureId={id} t={t} />}
          {tab === "reschedule" && <ReschedulePanel fixture={fixture} refresh={refresh} t={t} />}
        </>
      )}

      {/* Read-only chat for confirmed fixture (officers can still chat) */}
      {isOfficer && fixture.status === "confirmed" && (
        <div className="space-y-2">
          <div className="px-1 text-[10px] font-mono uppercase tracking-widest text-[#00FF87]">{t("pcl.fx.tabChat")}</div>
          <ChatPanel fixtureId={id} t={t} />
        </div>
      )}
    </div>
  );
}

/* ============================================================== */
function TeamBadge({ team, align, highlight }) {
  if (!team) return <div />;
  return (
    <div className={`flex flex-col items-center gap-1 ${highlight ? "" : ""}`}>
      <div className="text-3xl">{team.crest_emoji || "⚽"}</div>
      <div className={`text-[11px] font-extrabold text-center truncate w-full ${highlight ? "text-[#FFD700]" : "text-white"}`}>{team.name}</div>
    </div>
  );
}

function StatusPill({ status, t }) {
  const map = {
    scheduled:        { c: "bg-[#FFD700]/20 text-[#FFD700]", k: "pcl.status.scheduled" },
    awaiting_scores:  { c: "bg-[#60A5FA]/20 text-[#60A5FA]", k: "pcl.status.awaiting" },
    confirmed:        { c: "bg-[#00FF87]/20 text-[#00FF87]", k: "pcl.status.confirmed" },
    disputed:         { c: "bg-[#E11D48]/20 text-[#F87171]", k: "pcl.status.disputed" },
  };
  const m = map[status] || { c: "bg-white/10 text-white/60", k: null };
  const label = m.k ? t(m.k) : status;
  return (
    <div className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest ${m.c}`}>
      {label}
    </div>
  );
}

/* ============================================================== */
function SubmitForm({ fixture, refresh, t }) {
  const myTeam = fixture.viewer_team === "home" ? fixture.home_team : fixture.away_team;
  const oppTeam = fixture.viewer_team === "home" ? fixture.away_team : fixture.home_team;
  const mySub = fixture.viewer_team === "home" ? fixture.home_submission : fixture.away_submission;
  const oppSub = fixture.viewer_team === "home" ? fixture.away_submission : fixture.home_submission;

  // members of my team available as scorers
  const teamMembers = [myTeam.captain, ...myTeam.deputies, ...myTeam.players].filter(Boolean);

  const [myScore, setMyScore] = useState(mySub ? mySub.my_score : 0);
  const [oppScore, setOppScore] = useState(mySub ? mySub.opp_score : 0);
  const [scorers, setScorers] = useState(mySub?.scorers || []);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const totalScorers = scorers.reduce((acc, s) => acc + (parseInt(s.count, 10) || 0), 0);
  const sumOk = totalScorers === Number(myScore);

  const addScorer = () => {
    setScorers((s) => [...s, { user_id: "", own_goal: false, count: 1 }]);
  };
  const updateScorer = (idx, patch) => {
    setScorers((s) => s.map((row, i) => i === idx ? { ...row, ...patch } : row));
  };
  const removeScorer = (idx) => setScorers((s) => s.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (mySub) return toast.error(t("pcl.sub.alreadySent"));
    if (!file) return toast.error(t("pcl.sub.needScreenshot"));
    if (!sumOk) return toast.error(t("pcl.sub.sumMismatch"));
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("my_score", String(myScore));
      fd.append("opp_score", String(oppScore));
      fd.append("scorers", JSON.stringify(scorers.map((s) => ({
        user_id: s.own_goal ? null : (s.user_id || null),
        own_goal: !!s.own_goal,
        count: parseInt(s.count, 10) || 0,
      }))));
      fd.append("screenshot", file);
      const { data } = await api.post(`/proclubs/fixtures/${fixture.id}/submit`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.status === "confirmed") toast.success(t("pcl.sub.confirmed"));
      else if (data?.status === "disputed") toast.warning(t("pcl.sub.disputed"));
      else toast.success(t("pcl.sub.recorded"));
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("pcl.sub.failed"));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="v2-pc-submit-form">
      <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 space-y-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#00FF87]">
          {t("pcl.sub.title")} — {myTeam.crest_emoji} {myTeam.name}
        </div>
        {mySub && (
          <div className="text-[10px] text-[#FFD700] flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" /> {t("pcl.sub.alreadySent")}
          </div>
        )}
        {oppSub && !mySub && (
          <div className="text-[10px] text-[#60A5FA]">{t("pcl.sub.oppSubmitted")}</div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <NumberInput label={`${t("pcl.sub.yourGoals")} (${myTeam.name})`} value={myScore} setValue={setMyScore} disabled={!!mySub} tid="v2-pc-sub-my-score" />
          <NumberInput label={`${t("pcl.sub.oppGoals")} (${oppTeam.name})`} value={oppScore} setValue={setOppScore} disabled={!!mySub} tid="v2-pc-sub-opp-score" />
        </div>
      </div>

      {/* Scorers */}
      <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#00FF87]">{t("pcl.sub.scorers")}</div>
          <div className={`text-[10px] font-mono ${sumOk ? "text-[#00FF87]" : "text-[#FFD700]"}`}>
            {totalScorers}/{myScore} {t("pcl.sub.goals")}
          </div>
        </div>
        {scorers.length === 0 && (
          <div className="text-[11px] text-white/50">{t("pcl.sub.noScorers")}</div>
        )}
        {scorers.map((s, idx) => (
          <div key={idx} className="grid grid-cols-[1fr_70px_36px] gap-2 items-center" data-testid={`v2-pc-scorer-row-${idx}`}>
            <select
              value={s.own_goal ? "__own__" : (s.user_id || "")}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "__own__") updateScorer(idx, { own_goal: true, user_id: "" });
                else updateScorer(idx, { own_goal: false, user_id: v });
              }}
              disabled={!!mySub}
              data-testid={`v2-pc-scorer-user-${idx}`}
              style={{ fontSize: "16px" }}
              className="bg-white/5 ring-1 ring-white/10 rounded-lg px-2 py-1.5 text-white outline-none focus:ring-[#00FF87]"
            >
              <option value="">— {t("pcl.sub.pickPlayer")} —</option>
              {teamMembers.map((m) => (
                <option key={m.id} value={m.id}>@{m.username}</option>
              ))}
              <option value="__own__">⚠ {t("pcl.sub.ownGoal")}</option>
            </select>
            <input type="number" min={1} max={20} value={s.count}
              onChange={(e) => updateScorer(idx, { count: e.target.value })}
              disabled={!!mySub}
              data-testid={`v2-pc-scorer-count-${idx}`}
              style={{ fontSize: "16px" }}
              className="bg-white/5 ring-1 ring-white/10 rounded-lg px-2 py-1.5 text-white text-center outline-none focus:ring-[#00FF87]" />
            <button type="button" onClick={() => removeScorer(idx)} disabled={!!mySub}
              data-testid={`v2-pc-scorer-remove-${idx}`}
              className="p-2 rounded-lg bg-[#E11D48]/20 text-[#F87171] disabled:opacity-30">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {!mySub && Number(myScore) > 0 && (
          <button type="button" onClick={addScorer} data-testid="v2-pc-scorer-add"
            className="w-full mt-1 inline-flex items-center justify-center gap-1 py-2 rounded-full bg-white/5 hover:bg-white/10 text-[11px] font-bold uppercase tracking-widest text-white">
            <Plus className="w-3.5 h-3.5" /> {t("pcl.sub.addScorer")}
          </button>
        )}
      </div>

      {/* Screenshot */}
      {!mySub && (
        <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 space-y-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#00FF87]">{t("pcl.sub.screenshot")}</div>
          <input ref={fileRef} type="file" accept="image/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="v2-pc-sub-file"
            className="hidden" />
          <button type="button" onClick={() => fileRef.current?.click()}
            data-testid="v2-pc-sub-file-pick"
            className="w-full py-2.5 rounded-full bg-white/10 hover:bg-white/15 text-[11px] font-bold uppercase tracking-widest text-white inline-flex items-center justify-center gap-2">
            <Upload className="w-4 h-4" /> {file ? file.name.slice(0, 30) : t("pcl.sub.pickFile")}
          </button>
        </div>
      )}

      {!mySub && (
        <button type="submit" disabled={busy || !sumOk || !file}
          data-testid="v2-pc-submit-btn"
          className="w-full py-3 rounded-full bg-[#00FF87] text-[#06120A] text-[12px] font-extrabold uppercase tracking-widest disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t("pcl.sub.submitBtn")}
        </button>
      )}
    </form>
  );
}

function NumberInput({ label, value, setValue, disabled, tid }) {
  return (
    <label className="block text-[10px] font-mono uppercase tracking-widest text-white/70">
      {label}
      <input type="number" min={0} max={99} value={value}
        onChange={(e) => setValue(parseInt(e.target.value || "0", 10))}
        disabled={disabled}
        data-testid={tid}
        style={{ fontSize: "20px" }}
        className="mt-1 w-full bg-white/5 ring-1 ring-white/10 rounded-xl px-3 py-2 text-white text-center font-extrabold outline-none focus:ring-[#00FF87] disabled:opacity-60" />
    </label>
  );
}

/* ============================================================== */
function ChatPanel({ fixtureId, t }) {
  const [msgs, setMsgs] = useState(null);
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/proclubs/fixtures/${fixtureId}/chat`);
      setMsgs(data.messages || []);
    } catch {
      setMsgs([]);
    }
  }, [fixtureId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const i = setInterval(load, 8000);
    return () => clearInterval(i);
  }, [load]);

  useEffect(() => {
    if (msgs && endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
  }, [msgs?.length]);

  const sendText = async (c) => {
    if (!c || !c.trim()) return;
    setBusy(true);
    try {
      await api.post(`/proclubs/fixtures/${fixtureId}/chat`, { content: c.trim() });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("pcl.chat.failed"));
      throw err;
    } finally { setBusy(false); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    try {
      await sendText(content);
      setContent("");
    } catch { /* error already toasted */ }
  };

  if (msgs === null) return <CenterLoader />;

  return (
    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 overflow-hidden flex flex-col" data-testid="v2-pc-chat">
      <div className="max-h-[400px] overflow-y-auto p-3 space-y-2">
        {msgs.length === 0 ? (
          <div className="text-[11px] text-white/50 text-center py-8">{t("pcl.chat.empty")}</div>
        ) : msgs.map((m) => (
          <div key={m.id} className="rounded-xl bg-white/5 px-3 py-2" data-testid={`v2-pc-chat-msg-${m.id}`}>
            <div className="flex items-center justify-between text-[9px] font-mono text-[#71717A]">
              <span>@{m.username} <span className="text-[#00FF87]">· {m.team_name}</span></span>
              <span>{new Date(m.created_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
            </div>
            <div className="text-sm text-white whitespace-pre-wrap break-words mt-0.5">{m.content}</div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="border-t border-white/10 p-2 space-y-2">
        <QuickReplyChips onSend={sendText} disabled={busy} testIdPrefix="v2-pc-chat-quick" />
        <div className="flex gap-2">
          <input type="text" value={content} onChange={(e) => setContent(e.target.value)}
            placeholder={t("pcl.chat.placeholder")} maxLength={1000}
            data-testid="v2-pc-chat-input"
            style={{ fontSize: "16px" }}
            className="flex-1 bg-white/5 ring-1 ring-white/10 rounded-full px-3 py-2 text-white outline-none focus:ring-[#00FF87]" />
          <button type="submit" disabled={busy || !content.trim()}
            data-testid="v2-pc-chat-send"
            className="px-3 py-2 rounded-full bg-[#00FF87] text-[#06120A] disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ============================================================== */
function ReschedulePanel({ fixture, refresh, t }) {
  const [proposals, setProposals] = useState([]);
  const [newAt, setNewAt] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    // We don't have a dedicated list endpoint; rely on fixture last_rescheduled_at +
    // a single-row local store of the most recent proposal returned by the POST.
    // For now, polling not strictly needed.
  }, []);

  useEffect(() => { load(); }, [load]);

  const propose = async (e) => {
    e.preventDefault();
    if (!newAt) return toast.error(t("pcl.rs.pickDate"));
    setBusy(true);
    try {
      const iso = new Date(newAt).toISOString();
      const { data } = await api.post(`/proclubs/fixtures/${fixture.id}/reschedule`, { new_at: iso });
      setProposals((p) => [data.proposal, ...p]);
      toast.success(t("pcl.rs.proposed"));
      setNewAt("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("pcl.rs.failed"));
    } finally { setBusy(false); }
  };

  const respond = async (pid, action) => {
    try {
      await api.post(`/proclubs/reschedule/${pid}/${action}`);
      toast.success(action === "accept" ? t("pcl.rs.accepted") : t("pcl.rs.declined"));
      setProposals((arr) => arr.map((p) => p.id === pid ? { ...p, status: action === "accept" ? "accepted" : "declined" } : p));
      if (action === "accept") refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("pcl.rs.failed"));
    }
  };

  return (
    <div className="space-y-3" data-testid="v2-pc-resch">
      <form onSubmit={propose} className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 space-y-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#00FF87]">{t("pcl.rs.proposeTitle")}</div>
        <p className="text-[11px] text-white/60">{t("pcl.rs.proposeBody")}</p>
        <input type="datetime-local" value={newAt} onChange={(e) => setNewAt(e.target.value)}
          data-testid="v2-pc-resch-date"
          style={{ fontSize: "16px" }}
          className="w-full bg-white/5 ring-1 ring-white/10 rounded-xl px-3 py-2 text-white outline-none focus:ring-[#00FF87]" />
        <button type="submit" disabled={busy || !newAt}
          data-testid="v2-pc-resch-submit"
          className="w-full py-2 rounded-full bg-[#FFD700] text-[#06120A] text-[11px] font-extrabold uppercase tracking-widest disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t("pcl.rs.proposeBtn")}
        </button>
      </form>

      {proposals.length > 0 && (
        <div className="space-y-2">
          <div className="px-1 text-[10px] font-mono uppercase tracking-widest text-[#71717A]">{t("pcl.rs.recent")}</div>
          {proposals.map((p) => (
            <div key={p.id} className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-3 flex items-center gap-2"
              data-testid={`v2-pc-resch-row-${p.id}`}>
              <CalendarClock className="w-4 h-4 text-[#FFD700]" />
              <div className="flex-1 min-w-0">
                <div className="text-[12px] text-white">{new Date(p.new_at).toLocaleString()}</div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-white/50">{p.status}</div>
              </div>
              {p.status === "pending" && (
                <>
                  <button type="button" onClick={() => respond(p.id, "accept")}
                    data-testid={`v2-pc-resch-accept-${p.id}`}
                    className="px-2 py-1 rounded-full bg-[#00FF87] text-[#06120A] text-[9px] font-extrabold uppercase tracking-widest">
                    {t("pcl.rs.acceptBtn")}
                  </button>
                  <button type="button" onClick={() => respond(p.id, "decline")}
                    data-testid={`v2-pc-resch-decline-${p.id}`}
                    className="px-2 py-1 rounded-full bg-[#E11D48]/20 text-[#F87171] text-[9px] font-bold uppercase tracking-widest">
                    {t("pcl.rs.declineBtn")}
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================== */
function ConfirmedScorersView({ fixture, t }) {
  const homeSc = fixture.scorers?.home || [];
  const awaySc = fixture.scorers?.away || [];

  const renderSide = (label, team, scorers) => (
    <div className="space-y-1">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#71717A]">{label} — {team?.name}</div>
      {scorers.length === 0 ? (
        <div className="text-[11px] text-white/40">—</div>
      ) : scorers.map((s, idx) => (
        <div key={idx} className="text-[11px] text-white flex items-center gap-1">
          <Trophy className="w-3 h-3 text-[#FFD700]" />
          {s.own_goal ? <span className="text-[#F87171]">{t("pcl.sub.ownGoal")}</span>
            : <span>@{lookupName(team, s.user_id)}</span>}
          <span className="text-white/60 ml-auto">× {s.count}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 space-y-3" data-testid="v2-pc-fx-scorers">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#00FF87]">{t("pcl.fx.scorers")}</div>
      <div className="grid grid-cols-2 gap-3">
        {renderSide(t("pcl.fx.home"), fixture.home_team, homeSc)}
        {renderSide(t("pcl.fx.away"), fixture.away_team, awaySc)}
      </div>
    </div>
  );
}

function lookupName(team, uid) {
  if (!team || !uid) return uid?.slice(0, 6) || "?";
  const all = [team.captain, ...(team.deputies || []), ...(team.players || [])].filter(Boolean);
  const m = all.find((x) => x?.id === uid);
  return m?.username || uid.slice(0, 6);
}

function CenterLoader() {
  return <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#00FF87]" /></div>;
}
